import { createSelector } from 'reselect';
import {
  FavouritesExperienceSuccessState,
  FavouritesExperienceState,
} from 'redux/modules/favourites-experience/types';
import type { CategoryLevelFilter } from 'constants/products';
import { SORT_URL_CATEGORY_RANKING } from 'redux/modules/favourites-experience/constants';
import { getBoughtInStore } from './get-bought-in-store';
import { getBoughtInStoreOfferFilterActiveState } from './get-bought-in-store-offers';

export const isBoughtInStoreSuccessfullyLoaded = (
  state: FavouritesExperienceState,
): state is FavouritesExperienceSuccessState => 'categories' in state;

export const getIsBoughtInStoreSuccessfullyLoaded = createSelector(
  [getBoughtInStore],
  isBoughtInStoreSuccessfullyLoaded,
);

export const getBoughtInStoreLoading = createSelector(
  getBoughtInStore,
  boughtInStore => boughtInStore.loading,
);

export const getBoughtInStoreSortingMethod = createSelector(
  [getBoughtInStore],
  boughtInStore => boughtInStore.sortBy || SORT_URL_CATEGORY_RANKING,
);

export const getBoughtInStoreCategoryIdFilter = createSelector(
  getBoughtInStore,
  boughtInStore => boughtInStore.categoryIdFilter,
);

const ALL_CATEGORIES_LABEL = 'All Categories';
const DEFAULT_CATEGORY_LABEL = 'Filter by';
const EMPTY_BOUGHT_IN_STORE_ROOT_CATEGORIES_ARRAY: CategoryLevelFilter[] = [];

const getBoughtInStoreCategories = (state: WtrState): CategoryLevelFilter[] =>
  (state.boughtInStore as FavouritesExperienceSuccessState)?.rootCategories ||
  EMPTY_BOUGHT_IN_STORE_ROOT_CATEGORIES_ARRAY;

type CategoryOption = {
  text: string;
  value?: string;
  count?: number;
  deselector?: boolean;
};

export const getBoughtInStoreCategoryOptions = createSelector(
  [getBoughtInStoreCategories],
  (links: CategoryLevelFilter[]): CategoryOption[] => [
    {
      deselector: true,
      text: ALL_CATEGORIES_LABEL,
    },
    ...links.map((link: CategoryLevelFilter) => ({
      value: link.categoryId,
      text: link.name,
      count: link.expectedResults,
    })),
  ],
);

export const getBoughtInStoreCategoryLabel = createSelector(
  [getBoughtInStore, getBoughtInStoreCategoryOptions],
  (boughtInStore, options) => {
    const selected = options.find(option => option.value === boughtInStore.categoryIdFilter);
    return selected
      ? `${selected.text}${selected.count ? ` (${selected.count})` : ''}`
      : DEFAULT_CATEGORY_LABEL;
  },
);

export const isBoughtInStoreFiltered = (state: WtrState) =>
  !!getBoughtInStoreCategoryIdFilter(state) || !!getBoughtInStoreOfferFilterActiveState(state);

export const getBoughtInStoreLoadingMore = createSelector(
  getBoughtInStore,
  boughtInStore => boughtInStore.loadingMore,
);
